import React, { FunctionComponent } from "react";
import {
  Accordion,
  AccordionBody,
  AccordionButton,
  Icon,
  SidebarBody,
  SidebarHeader,
  SidebarLayout,
} from "../../bbprojects/components";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  ArchiveBoxIcon,
  TicketIcon,
  LockClosedIcon,
  TagIcon,
  UserGroupIcon,
  TableCellsIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  RectangleGroupIcon,
  MapPinIcon,
  CreditCardIcon,
  CalendarIcon,
  CircleStackIcon,
} from "@heroicons/react/24/solid";
import Logo from "./Logo";
import Version from "./Version";
import { useTranslation } from "react-i18next";
import { useAuthorizationContext } from "../../authentication/AuthorizationProvider";
import { configurationRoutes } from "../constants/configurationRoutes";
import {
  ButtonItem,
  IconChevronToggle,
  TextDescriptor,
  TextHeading,
} from "../../bbprojects/tables/bbprojects/components/base";

const SideBar: FunctionComponent = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { permissions } = useAuthorizationContext();

  const classes = {
    textDescriptor: "",
    accordionItems: "",
    accordionButton:
      "w-full h-full transition ease-in-out duration-300 hover:bg-opacity-darker",
  };

  const isConfigurationSectionEnabled =
    permissions.deliveryMethods?.readDeliveryMethodsTable ||
    permissions.productCategories?.readProductCategories ||
    permissions.ticketTemplates?.readTicketTemplatesTable ||
    permissions.salesChannels?.readSalesChannelsTable ||
    permissions.salesChannelUnits?.readSalesChannelUnitsTable ||
    permissions.checkpoints?.readCheckpointsTable ||
    permissions.checkpointTypes?.readCheckpointTypesTable ||
    permissions.paymentMethods?.readPaymentMethodsTable ||
    permissions.paymentProviders?.readPaymentProvidersTable;

  const getActiveClassName = (props: { isActive: boolean }) => {
    return props.isActive ? "active-sidebar-item" : undefined;
  };

  return (
    <div className="flex items-center">
      <SidebarLayout>
        <SidebarHeader>
          <Link to="/">
            <div className="mt-4 mb-8">
              <TextHeading text="" Icon={Logo} textCenter={true} />
            </div>
          </Link>
        </SidebarHeader>
        <SidebarBody>
          <div>
            <TextDescriptor
              text={t("SideBar.Sections").toUpperCase()}
              className="ml-4"
            />
            {permissions.tickets?.readTicketTable && (
              <NavLink
                to="/tickets"
                className={({ isActive }) =>
                  isActive ||
                  location.pathname.includes("/tickets") ||
                  location.pathname.includes("/purchases")
                    ? "active-sidebar-item"
                    : undefined
                }
                data-testid="navTickets"
              >
                <ButtonItem text={t("Tickets")} Icon={TicketIcon} />
              </NavLink>
            )}
            {permissions.tickets?.createBulkPurchase && (
              <NavLink
                to="/bulk-tickets"
                className={getActiveClassName}
                data-testid="navBulkPurchase"
              >
                <ButtonItem text={t("BulkPurchases")} Icon={TicketIcon} />
              </NavLink>
            )}
            {permissions.customers?.readCustomerTable && (
              <NavLink
                to="/customers"
                className={getActiveClassName}
                data-testid="navCustomers"
              >
                <ButtonItem text={t("Customers")} Icon={UserGroupIcon} />
              </NavLink>
            )}
            {permissions.accounts?.readAccountsTable && (
              <NavLink
                to="/customer-accounts"
                className={getActiveClassName}
                data-testid="navAccounts"
              >
                <ButtonItem text={t("Accounts")} Icon={LockClosedIcon} />
              </NavLink>
            )}
            {permissions.licensePlates?.readLicensePlatesTable && (
              <NavLink
                to="/license-plates"
                className={getActiveClassName}
                data-testid="navLicensePlates"
              >
                <ButtonItem text={t("LicensePlates")} Icon={TableCellsIcon} />
              </NavLink>
            )}
            {permissions.products?.readProductsTable && (
              <NavLink
                to="/products"
                className={getActiveClassName}
                data-testid="navProducts"
              >
                <ButtonItem text={t("Products")} Icon={ArchiveBoxIcon} />
              </NavLink>
            )}
            {permissions.financialTransactions
              ?.readFinancialTransactionsTable && (
              <NavLink
                to="/financial-transactions"
                className={getActiveClassName}
                data-testid="navFinancialTransactions"
              >
                <ButtonItem
                  text={t("FinancialTransactions")}
                  Icon={CurrencyDollarIcon}
                />
              </NavLink>
            )}
            {permissions.shareholderMeetings?.readShareholderMeetingsTable && (
              <NavLink
                to="/shareholder-meetings"
                className={getActiveClassName}
                data-testid="navShareholderMeetings"
              >
                <ButtonItem
                  text={t("ShareholderMeetings")}
                  Icon={CalendarIcon}
                />
              </NavLink>
            )}
            {isConfigurationSectionEnabled ? (
              <Accordion defaultOpen={configurationRoutes.includes(pathname)}>
                {({ open }) => (
                  <>
                    <AccordionButton>
                      <div className={classes.accordionButton}>
                        <div className="w-full h-full transition ease-in-out duration-300 hover:bg-opacity-darker">
                          <div className="flex items-center overflow-hidden px-4 py-2">
                            <Icon
                              Icon={ClipboardDocumentListIcon}
                              classNameIcon="w-4 h-4 text-black-light mr-1 mr-3 order-0"
                            />
                            <div
                              data-testid="navConfiguration"
                              className="truncate font-primary text-base text-black-light"
                            >
                              {t("Configuration")}
                            </div>
                            <IconChevronToggle isOpen={!open} right />
                          </div>
                        </div>
                      </div>
                    </AccordionButton>
                    <AccordionBody>
                      <div className="pl-4">
                        {permissions.productCategories
                          ?.readProductCategories && (
                          <NavLink
                            to="/product-categories"
                            className={({ isActive }) =>
                              isActive ? "active-sidebar-item" : undefined
                            }
                            data-testid="navProductCategories"
                          >
                            <ButtonItem
                              text={t("ProductCategories")}
                              Icon={TagIcon}
                            />
                          </NavLink>
                        )}
                        {permissions.deliveryMethods
                          ?.readDeliveryMethodsTable && (
                          <NavLink
                            to="/delivery-methods"
                            className={({ isActive }) =>
                              isActive ? "active-sidebar-item" : undefined
                            }
                            data-testid="navDeliveryMethods"
                          >
                            <ButtonItem
                              text={t("DeliveryMethods")}
                              Icon={TagIcon}
                            />
                          </NavLink>
                        )}

                        {permissions.ticketTemplates
                          ?.readTicketTemplatesTable && (
                          <NavLink
                            to="/ticket-templates"
                            className={({ isActive }) =>
                              isActive ? "active-sidebar-item" : undefined
                            }
                            data-testid="navTicketTemplates"
                          >
                            <ButtonItem
                              text={t("TicketTemplates")}
                              Icon={RectangleGroupIcon}
                            />
                          </NavLink>
                        )}

                        {permissions.salesChannels?.readSalesChannelsTable && (
                          <NavLink
                            to="/sales-channels"
                            className={({ isActive }) =>
                              isActive ? "active-sidebar-item" : undefined
                            }
                            data-testid="navSalesChannel"
                          >
                            <ButtonItem
                              text={t("SalesChannels")}
                              Icon={ArchiveBoxIcon}
                            />
                          </NavLink>
                        )}

                        {permissions.salesChannelUnits
                          ?.readSalesChannelUnitsTable && (
                          <NavLink
                            to="/sales-channel-units"
                            className={({ isActive }) =>
                              isActive ? "active-sidebar-item" : undefined
                            }
                            data-testid="navSalesChannelUnit"
                          >
                            <ButtonItem
                              text={t("SalesChannelUnits")}
                              Icon={ArchiveBoxIcon}
                            />
                          </NavLink>
                        )}

                        {permissions.checkpoints?.readCheckpointsTable && (
                          <NavLink
                            to="/checkpoints"
                            className={({ isActive }) =>
                              isActive ? "active-sidebar-item" : undefined
                            }
                            data-testid="navCheckpoints"
                          >
                            <ButtonItem
                              text={t("Checkpoints")}
                              Icon={MapPinIcon}
                            />
                          </NavLink>
                        )}

                        {permissions.checkpointTypes
                          ?.readCheckpointTypesTable && (
                          <NavLink
                            to="/checkpoint-types"
                            className={({ isActive }) =>
                              isActive ? "active-sidebar-item" : undefined
                            }
                            data-testid="navCheckpointTypes"
                          >
                            <ButtonItem
                              text={t("CheckpointTypes")}
                              Icon={MapPinIcon}
                            />
                          </NavLink>
                        )}

                        {permissions.paymentMethods
                          ?.readPaymentMethodsTable && (
                          <NavLink
                            to="/payment-methods"
                            className={({ isActive }) =>
                              isActive ? "active-sidebar-item" : undefined
                            }
                            data-testid="navPaymentMethods"
                          >
                            <ButtonItem
                              text={t("PaymentMethods")}
                              Icon={CreditCardIcon}
                            />
                          </NavLink>
                        )}

                        {permissions.paymentProviders
                          ?.readPaymentProvidersTable && (
                          <NavLink
                            to="/payment-providers"
                            className={({ isActive }) =>
                              isActive ? "active-sidebar-item" : undefined
                            }
                            data-testid="navPaymentProviders"
                          >
                            <ButtonItem
                              text={t("PaymentProviders")}
                              Icon={CreditCardIcon}
                            />
                          </NavLink>
                        )}

                        {permissions.validitySchedules
                          ?.readValiditySchedulesTable && (
                          <NavLink
                            to="/validity-schedules"
                            className={({ isActive }) =>
                              isActive ? "active-sidebar-item" : undefined
                            }
                            data-testid="navValiditySchedules"
                          >
                            <ButtonItem
                              text={t("ValiditySchedules")}
                              Icon={CalendarIcon}
                            />
                          </NavLink>
                        )}

                        {permissions.cacheInvalidation?.invalidateCache && (
                          <NavLink
                            to="/cache-invalidation"
                            className={({ isActive }) =>
                              isActive ? "active-sidebar-item" : undefined
                            }
                            data-testid="navCacheInvalidation"
                          >
                            <ButtonItem
                              text={t("CacheInvalidation")}
                              Icon={CircleStackIcon}
                            />
                          </NavLink>
                        )}
                      </div>
                    </AccordionBody>
                  </>
                )}
              </Accordion>
            ) : null}
          </div>
        </SidebarBody>
        <Version />
      </SidebarLayout>
    </div>
  );
};

export default SideBar;
